import { z } from 'zod'
import { supabaseClient } from '~/shared/common/supabase/client'
import { typesenseClient } from '~/client/common/typesense/client'
import { getVenueFacetCountsFromTypesense } from '~/client/space/space-list.api'
import { VenueTagVenueSearchResponseFromTypesense } from './venue-tag.typesense.schema'
import type { ListSpacesParams } from '~/shared/space/space-list.params'
import {
  AddMembershipInput,
  AddMembershipOutput,
  CreateGroupInput,
  CreateGroupOutput,
  CreateTagInput,
  CreateTagOutput,
  DeleteGroupInput,
  DeleteGroupOutput,
  DeleteTagInput,
  DeleteTagOutput,
  ListTaggedVenuesInput,
  ListTaggedVenuesOutput,
  ListVenueTagsOutput,
  NotifyTagAddedInput,
  NotifyTagAddedOutput,
  RemoveMembershipInput,
  RemoveMembershipOutput,
  ReorderGroupsInput,
  ReorderGroupsOutput,
  ReorderTagsInput,
  ReorderTagsOutput,
  UpdateGroupInput,
  UpdateGroupOutput,
  UpdateTagInput,
  UpdateTagOutput,
} from '~/shared/venue-tag/venue-tag.api.schema'

const getAuthHeader = async (): Promise<string> => {
  const {
    data: { session },
  } = await supabaseClient.auth.getSession()
  if (!session?.access_token) {
    throw new Error('Not authenticated')
  }
  return `Bearer ${session.access_token}`
}

const fetchJson = async <T>(
  path: string,
  method: 'GET' | 'POST',
  body: unknown,
  parser: z.ZodType<T>,
): Promise<T> => {
  const res = await fetch(path, {
    method,
    headers: {
      ...(method === 'POST' ? { 'Content-Type': 'application/json' } : {}),
      Authorization: await getAuthHeader(),
    },
    body: method === 'POST' ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(errBody.error ?? `${path} ${res.status}`)
  }
  return parser.parse(await res.json())
}

export const fetchVenueTagGroups = () =>
  fetchJson('/api/venue-tag/list', 'GET', undefined, ListVenueTagsOutput)

// Tags
export const submitCreateTag = (input: z.infer<typeof CreateTagInput>) =>
  fetchJson('/api/venue-tag/tag/create', 'POST', input, CreateTagOutput)

export const submitUpdateTag = (input: z.infer<typeof UpdateTagInput>) =>
  fetchJson('/api/venue-tag/tag/update', 'POST', input, UpdateTagOutput)

export const submitDeleteTag = (input: z.infer<typeof DeleteTagInput>) =>
  fetchJson('/api/venue-tag/tag/delete', 'POST', input, DeleteTagOutput)

export const submitReorderTags = (input: z.infer<typeof ReorderTagsInput>) =>
  fetchJson('/api/venue-tag/tag/reorder', 'POST', input, ReorderTagsOutput)

// Groups
export const submitCreateGroup = (input: z.infer<typeof CreateGroupInput>) =>
  fetchJson('/api/venue-tag/group/create', 'POST', input, CreateGroupOutput)

export const submitUpdateGroup = (input: z.infer<typeof UpdateGroupInput>) =>
  fetchJson('/api/venue-tag/group/update', 'POST', input, UpdateGroupOutput)

export const submitDeleteGroup = (input: z.infer<typeof DeleteGroupInput>) =>
  fetchJson('/api/venue-tag/group/delete', 'POST', input, DeleteGroupOutput)

export const submitReorderGroups = (input: z.infer<typeof ReorderGroupsInput>) =>
  fetchJson('/api/venue-tag/group/reorder', 'POST', input, ReorderGroupsOutput)

// Venues
export const fetchTaggedVenues = (input: z.input<typeof ListTaggedVenuesInput>) =>
  fetchJson('/api/venue-tag/tag/venues', 'POST', input, ListTaggedVenuesOutput)

export const submitAddMembership = (input: z.infer<typeof AddMembershipInput>) =>
  fetchJson('/api/venue-tag/membership/add', 'POST', input, AddMembershipOutput)

export const submitRemoveMembership = (input: z.infer<typeof RemoveMembershipInput>) =>
  fetchJson('/api/venue-tag/membership/remove', 'POST', input, RemoveMembershipOutput)

export const submitNotifyTagAdded = (input: z.infer<typeof NotifyTagAddedInput>) =>
  fetchJson('/api/venue-tag/notify-tag-added', 'POST', input, NotifyTagAddedOutput)

export type VenueSearchResult = { id: number; name: string; address: string }

const toVenueFilter = (excludeVenueIds: ReadonlyArray<number>, venueGroupIds: ReadonlyArray<number>): string | undefined => {
  // Exclude `customer_ghost` rows — those are Google-Places-derived stubs that
  // exist in `main_venue` only as placeholders. They aren't real bookable
  // venues and inserting a tag membership against them either FKs to nothing
  // or creates dangling links the rest of the app won't surface.
  const filters: string[] = ['create_source:!=customer_ghost']
  if (excludeVenueIds.length > 0) {
    filters.push(`biblio_venue_id:!=[${excludeVenueIds.join(',')}]`)
  }
  if (venueGroupIds.length > 0) {
    filters.push(`venue_group.id:=[${venueGroupIds.join(',')}]`)
  }
  return filters.join(' && ')
}

/**
 * One-shot Typesense facet that maps each venue id in `venueIds` to its space
 * count, scoped to the user's active venue_group when provided. Used by the
 * SRP tag filter to show "N spaces" next to each tag (rather than N venues).
 *
 * Returns `{}` when there are no ids to look up or no matching spaces.
 */
export const fetchSpaceCountsByVenueId = (
  venueIds: ReadonlyArray<number>,
  activeVenueGroupId: number | null | undefined,
): Promise<Record<number, number>> => getVenueFacetCountsFromTypesense(
  typeof activeVenueGroupId === 'number' && activeVenueGroupId > 0 ? { active_venue_group_id: activeVenueGroupId } : {},
  venueIds,
)

export const fetchVenueTagFacetCountsByVenueId = (
  venueIds: ReadonlyArray<number>,
  params: ListSpacesParams,
  options?: { signal?: AbortSignal },
): Promise<Record<number, number>> => getVenueFacetCountsFromTypesense(params, venueIds, options)

// Plain Typesense full-text search on the `venues` collection, ranked by
// venue NAME only. Tag memberships reference `main_venue.id`, so this never
// touches Google Maps / Places — we can only tag rows that already exist in
// our own DB. The Typesense doc's `biblio_venue_id` is the main_venue.id.
export const searchVenuesByName = async (
  query: string,
  excludeVenueIds: ReadonlyArray<number>,
  venueGroupIds: ReadonlyArray<number>,
  limit = 8,
): Promise<VenueSearchResult[]> => {
  const trimmed = query.trim()
  if (!trimmed) return []

  const response = await typesenseClient
    .collections('venues')
    .documents()
    .search({
      q: trimmed,
      query_by: 'name',
      per_page: limit,
      num_typos: 2,
      prefix: true,
      include_fields: 'id,biblio_venue_id,name,display_address,venue_group.id',
      filter_by: toVenueFilter(excludeVenueIds, venueGroupIds),
    })

  const result = VenueTagVenueSearchResponseFromTypesense.parse(response)
  return (result.hits ?? [])
    .map((hit) => hit.document)
    .filter((venue) => Number.isFinite(venue.id) && venue.id > 0)
}
